import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../Components/AuthProvider";
import { Pagination } from "react-bootstrap";

import {
    Card,
    CardBody,
    Col,
    FormGroup,
    Row,
    Label,
    Input,
    Button,
    Table
} from "reactstrap";

import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    faFileExcel,
    faReceipt,
    faSearch,
    faSyncAlt,
    faBoxOpen,
    faPlaneArrival,
    faPlaneDeparture,
    faBoxesStacked
} from "@fortawesome/free-solid-svg-icons";

import ReactLoading from "react-loading";
import Rate_Chart_Service from "../services/Rate_Chart_Service";

import "./StockReport.css";


export default function StockReport(props) {

    const {
        userId,
        branchId,
        companyid
    } = useContext(AuthContext);

    const navigate = useNavigate();
    const { isAuthenticated } = useContext(AuthContext);


    /* =========================================================
       AUTHENTICATION
    ========================================================= */

    useEffect(() => {

        if (!isAuthenticated) {
            navigate(
                "/login?message=You need to be authenticated to access this page."
            );
        }

    }, [isAuthenticated, navigate]);


    /* =========================================================
       DATASETS
    ========================================================= */

    const [stockData, setStockData] = useState({
        import: [],
        export: [],
        subImport: [],
        subExport: []
    });


    /* =========================================================
       ACTIVE TAB
    ========================================================= */

    const [activeTab, setActiveTab] = useState("import");


    /* =========================================================
       PAGINATION
       Independent pagination for each table
    ========================================================= */

    const itemsPerPage = 25;

    const [currentPages, setCurrentPages] = useState({
        import: 1,
        export: 1,
        subImport: 1,
        subExport: 1
    });


    /* =========================================================
       TOTALS
    ========================================================= */

    const [totals, setTotals] = useState({
        import: {
            packages: 0,
            weight: 0
        },
        export: {
            packages: 0,
            weight: 0
        },
        subImport: {
            packages: 0,
            weight: 0
        },
        subExport: {
            packages: 0,
            weight: 0
        }
    });


    /* =========================================================
       SEARCH
    ========================================================= */

    const initialSearchCriteria = {
        companyId: companyid,
        branchId: branchId,
        type: "ALL",
        location: "SEEPZ",
        holdStatus: "N"
    };


    const [searchCriteria, setSearchCriteria] =
        useState(initialSearchCriteria);

    const [loading, setLoading] = useState(false);


    /* =========================================================
       HOLD-DEPENDENT COLUMNS
       "Hold By" / "Hold Date" only make sense to show when the
       user is actually looking at held stock.
    ========================================================= */

    const showHoldColumns =
        searchCriteria.holdStatus === "Y" ||
        searchCriteria.holdStatus === "H";


    /* =========================================================
       LOADING OVERLAY
    ========================================================= */

    const styles = {
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.80)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
        }
    };


    /* =========================================================
       INITIAL LOAD
    ========================================================= */

    useEffect(() => {

        handleSearch(initialSearchCriteria);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /* =========================================================
       FORMAT DATE
    ========================================================= */

    const formatDate = (value) => {

        if (!value) {
            return "";
        }

        const date = new Date(value);

        if (isNaN(date.getTime())) {
            return "";
        }

        return `${date.getDate().toString().padStart(2, "0")}/${
            (date.getMonth() + 1).toString().padStart(2, "0")
        }/${date.getFullYear()}`;
    };


    /* =========================================================
       FORMAT WEIGHT
    ========================================================= */

    const formatWeight = (value, unit) => {

        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return "";
        }

        const weight = Number(value);

        if (isNaN(weight)) {
            return value;
        }

        return `${weight.toFixed(2)} ${unit || ""}`;
    };


    /* =========================================================
       CALCULATE TOTALS
    ========================================================= */

    const calculateDatasetTotals = (data, type) => {

        if (!Array.isArray(data)) {
            return {
                packages: 0,
                weight: 0
            };
        }


        let packages = 0;
        let weight = 0;


        data.forEach((row) => {

            if (
                type === "import" ||
                type === "subImport"
            ) {

                packages += Number(
                    row.nop || 0
                );

                weight += Number(
                    row.grossWeight ||
                    row.netWeight ||
                    0
                );

            } else {

                packages += Number(
                    row.noOfPackages ||
                    row.nop ||
                    0
                );

                weight += Number(
                    row.grossWeight ||
                    row.gwWeight ||
                    0
                );
            }

        });


        return {
            packages: Math.round(packages),
            weight: Number(weight.toFixed(2))
        };
    };


    /* =========================================================
       SEARCH API
    ========================================================= */

    const handleSearch = async (criteria) => {

        setLoading(true);

        try {

            const response =
                await Rate_Chart_Service.searchStockReport({
                    params: criteria
                });


            const data = response?.data || {};


            const updatedData = {
                import: Array.isArray(data.import)
                    ? data.import
                    : [],

                export: Array.isArray(data.export)
                    ? data.export
                    : [],

                subImport: Array.isArray(data.subImport)
                    ? data.subImport
                    : [],

                subExport: Array.isArray(data.subExport)
                    ? data.subExport
                    : []
            };


            setStockData(updatedData);


            /* Reset pagination */
            setCurrentPages({
                import: 1,
                export: 1,
                subImport: 1,
                subExport: 1
            });


            /* Calculate totals */
            setTotals({
                import: calculateDatasetTotals(
                    updatedData.import,
                    "import"
                ),

                export: calculateDatasetTotals(
                    updatedData.export,
                    "export"
                ),

                subImport: calculateDatasetTotals(
                    updatedData.subImport,
                    "subImport"
                ),

                subExport: calculateDatasetTotals(
                    updatedData.subExport,
                    "subExport"
                )
            });


            /*
             * Automatically open the first dataset
             * that contains records.
             */
            if (updatedData.import.length > 0) {

                setActiveTab("import");

            } else if (updatedData.export.length > 0) {

                setActiveTab("export");

            } else if (updatedData.subImport.length > 0) {

                setActiveTab("subImport");

            } else if (updatedData.subExport.length > 0) {

                setActiveTab("subExport");

            } else {

                setActiveTab("import");
            }


        } catch (error) {

            console.error(
                "Error loading stock report:",
                error
            );

            toast.error(
                "Oops something went wrong!",
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 1000
                }
            );

            setStockData({
                import: [],
                export: [],
                subImport: [],
                subExport: []
            });

        } finally {

            setLoading(false);
        }
    };


    /* =========================================================
       RESET
    ========================================================= */

    const handleReset = () => {

        setSearchCriteria(initialSearchCriteria);

        handleSearch(initialSearchCriteria);
    };


    /* =========================================================
       PAGE CHANGE
    ========================================================= */

    const handlePageChange = (type, page) => {

        const totalPages = Math.ceil(
            stockData[type].length / itemsPerPage
        );


        if (
            page >= 1 &&
            page <= totalPages
        ) {

            setCurrentPages(prev => ({
                ...prev,
                [type]: page
            }));
        }
    };


    /* =========================================================
       GET CURRENT PAGE DATA
    ========================================================= */

    const getCurrentItems = (type) => {

        const data = stockData[type] || [];

        const currentPage =
            currentPages[type] || 1;

        const start =
            (currentPage - 1) * itemsPerPage;

        const end =
            start + itemsPerPage;

        return data.slice(start, end);
    };


    /* =========================================================
       PAGE NUMBERS
    ========================================================= */

    const getDisplayPages = (type) => {

        const totalPages = Math.ceil(
            (stockData[type]?.length || 0) /
            itemsPerPage
        );

        const currentPage =
            currentPages[type] || 1;

        if (totalPages <= 5) {

            return Array.from(
                { length: totalPages },
                (_, index) => index + 1
            );
        }


        let start =
            Math.max(1, currentPage - 2);

        let end =
            Math.min(totalPages, currentPage + 2);


        if (start === 1) {
            end = 5;
        }

        if (end === totalPages) {
            start = totalPages - 4;
        }


        return Array.from(
            { length: end - start + 1 },
            (_, index) => start + index
        );
    };


    /* =========================================================
       PAGINATION COMPONENT
    ========================================================= */

    const renderPagination = (type) => {

        const totalRecords =
            stockData[type]?.length || 0;

        const totalPages =
            Math.ceil(totalRecords / itemsPerPage);

        const currentPage =
            currentPages[type] || 1;


        if (totalRecords <= itemsPerPage) {
            return null;
        }


        return (
            <div className="stock-pagination-wrapper">

                <div className="stock-pagination-info">
                    Showing{" "}
                    {((currentPage - 1) * itemsPerPage) + 1}
                    {" "}to{" "}
                    {Math.min(
                        currentPage * itemsPerPage,
                        totalRecords
                    )}
                    {" "}of{" "}
                    {totalRecords}
                </div>


                <Pagination className="stock-pagination">

                    <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() =>
                            handlePageChange(type, 1)
                        }
                    />


                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() =>
                            handlePageChange(
                                type,
                                currentPage - 1
                            )
                        }
                    />


                    {getDisplayPages(type).map(
                        page => (

                            <Pagination.Item
                                key={page}
                                active={
                                    page === currentPage
                                }
                                onClick={() =>
                                    handlePageChange(
                                        type,
                                        page
                                    )
                                }
                            >
                                {page}
                            </Pagination.Item>

                        )
                    )}


                    <Pagination.Next
                        disabled={
                            currentPage === totalPages
                        }
                        onClick={() =>
                            handlePageChange(
                                type,
                                currentPage + 1
                            )
                        }
                    />


                    <Pagination.Last
                        disabled={
                            currentPage === totalPages
                        }
                        onClick={() =>
                            handlePageChange(
                                type,
                                totalPages
                            )
                        }
                    />

                </Pagination>

            </div>
        );
    };


    /* =========================================================
       TAB CONFIGURATION
    ========================================================= */

    const tabs = [
        {
            key: "import",
            label: "IMPORT",
            icon: faPlaneArrival,
            color: "#2563eb"
        },
        {
            key: "export",
            label: "EXPORT",
            icon: faPlaneDeparture,
            color: "#059669"
        },
        {
            key: "subImport",
            label: "SUB-IMPORT",
            icon: faBoxesStacked,
            color: "#7c3aed"
        },
        {
            key: "subExport",
            label: "SUB-EXPORT",
            icon: faBoxOpen,
            color: "#ea580c"
        }
    ];


    /* =========================================================
       TAB HEADER
    ========================================================= */

    const renderTabs = () => {

        return (
            <div className="stock-tabs-container">

                {tabs.map(tab => {

                    const count =
                        stockData[tab.key]?.length || 0;

                    const isActive =
                        activeTab === tab.key;

                    const isSubTab =
                        tab.key === "subImport" ||
                        tab.key === "subExport";

                    /*
                     * Backend does not send sub data
                     * when holdStatus = Y.
                     */
                    const unavailable =
                        isSubTab &&
                        searchCriteria.holdStatus === "Y" &&
                        count === 0;


                    return (
                        <button
                            key={tab.key}
                            type="button"
                            className={`stock-tab ${
                                isActive
                                    ? "stock-tab-active"
                                    : ""
                            } ${
                                unavailable
                                    ? "stock-tab-disabled"
                                    : ""
                            }`}
                            onClick={() =>
                                setActiveTab(tab.key)
                            }
                        >

                            <span
                                className="stock-tab-icon"
                                style={{
                                    background:
                                        isActive
                                            ? tab.color
                                            : `${tab.color}18`,
                                    color:
                                        isActive
                                            ? "#fff"
                                            : tab.color
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={tab.icon}
                                />
                            </span>


                            <span className="stock-tab-content">

                                <span className="stock-tab-label">
                                    {tab.label}
                                </span>

                                <span className="stock-tab-count">
                                    {unavailable ? (
                                        "N/A"
                                    ) : (
                                        <>
                                            <strong>{count}</strong>
                                            {" "}Records

                                            <span className="stock-tab-count-dot" />

                                            <strong>
                                                {totals[tab.key]?.packages || 0}
                                            </strong>
                                            {" "}Pkgs
                                        </>
                                    )}
                                </span>

                            </span>

                        </button>
                    );

                })}

            </div>
        );
    };


    /* =========================================================
       EMPTY STATE
    ========================================================= */

    const renderEmptyState = (type) => {

        const isSub =
            type === "subImport" ||
            type === "subExport";


        const unavailable =
            isSub &&
            searchCriteria.holdStatus === "Y";


        return (
            <div className="stock-empty-state">

                <div className="stock-empty-icon">

                    <FontAwesomeIcon
                        icon={
                            unavailable
                                ? faBoxOpen
                                : faReceipt
                        }
                    />

                </div>


                <h5>
                    {unavailable
                        ? "Sub records are not applicable"
                        : "No records found"
                    }
                </h5>


                <p>
                    {unavailable
                        ? "Sub Import and Sub Export records are not returned for Hold = YES."
                        : "There are no stock records available for the selected criteria."
                    }
                </p>

            </div>
        );
    };


    /* =========================================================
       IMPORT TABLE
    ========================================================= */

    const renderImportTable = () => {

        const data = stockData.import;

        if (!data.length) {
            return renderEmptyState("import");
        }


        const currentItems =
            getCurrentItems("import");


        return (
            <div className="stock-table-wrapper">

                <div className="stock-table-title">

                    <div>
                        <h5>
                            <FontAwesomeIcon
                                icon={faPlaneArrival}
                            />
                            Import Stock
                        </h5>

                        <span>
                            {data.length} total records
                        </span>
                    </div>

                </div>


                <div className="table-responsive">

                    <Table
                        bordered
                        hover
                        className="stock-data-table"
                    >

                        <thead>
                            <tr>

                                <th>Sr No</th>
                                <th>MAWB</th>
                                <th>HAWB</th>
                                <th>IGM No</th>
                                <th>IGM Date</th>
                                <th>SIR No</th>
                                <th>SIR Date</th>
                                <th>Importer</th>
                                <th>Airline</th>
                                <th>Origin</th>
                                <th>Packages</th>
                                <th>Weight</th>
                                <th>NSDL</th>
                                <th>DGDC Status</th>
                                <th>DO Number</th>
                                <th>DO Date</th>
                                <th>Hold</th>
                                {showHoldColumns && (
                                    <>
                                        <th>Hold By</th>
                                        <th>Hold Date</th>
                                    </>
                                )}

                            </tr>
                        </thead>


                        <tbody>

                            {currentItems.map(
                                (item, index) => {

                                    const srNo =
                                        ((currentPages.import - 1) *
                                            itemsPerPage) +
                                        index + 1;


                                    return (
                                        <tr key={item.id || srNo}>

                                            <td>{srNo}</td>

                                            <td>
                                                {item.mawb}
                                            </td>

                                            <td>
                                                {item.hawb}
                                            </td>

                                            <td>
                                                {item.igmNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.igmDate
                                                )}
                                            </td>

                                            <td>
                                                {item.sirNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.sirDate
                                                )}
                                            </td>
                                            <td>
                                                {item.importernameOnParcel}
                                            </td>

                                            <td>
                                                {item.airlineName}
                                            </td>

                                            <td>
                                                {item.countryOrigin}
                                            </td>

                                            <td>
                                                {item.nop}
                                            </td>

                                            <td>
                                                {formatWeight(
                                                    item.grossWeight,
                                                    "GMS"
                                                )}
                                            </td>

                                            <td>
                                                {item.nsdlStatus}
                                            </td>

                                            <td>
                                                {item.dgdcStatus}
                                            </td>

                                            <td>
                                                {item.doNumber}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.doDate
                                                )}
                                            </td>

                                            <td>
                                                <span
                                                    className={
                                                        item.holdStatus === "Y"
                                                            ? "status-badge status-hold"
                                                            : "status-badge status-clear"
                                                    }
                                                >
                                                    {item.holdStatus === "Y"
                                                        ? "HOLD"
                                                        : "CLEAR"
                                                    }
                                                </span>
                                            </td>

                                            {showHoldColumns && (
                                                <>
                                                    <td>
                                                        {item.holdBy}
                                                    </td>

                                                    <td>
                                                        {formatDate(
                                                            item.holdDate
                                                        )}
                                                    </td>
                                                </>
                                            )}

                                        </tr>
                                    );

                                }
                            )}

                        </tbody>


                        <tfoot>

                            <tr>

                                <td
                                    colSpan="10"
                                    className="total-label"
                                >
                                    TOTAL
                                </td>

                                <td className="total-value">
                                    {totals.import.packages}
                                </td>

                                <td className="total-value">
                                    {totals.import.weight.toFixed(2)}
                                </td>

                                <td colSpan={showHoldColumns ? 7 : 5}></td>

                            </tr>

                        </tfoot>

                    </Table>

                </div>


                {renderPagination("import")}

            </div>
        );
    };


    /* =========================================================
       EXPORT TABLE
    ========================================================= */

    const renderExportTable = () => {

        const data = stockData.export;

        if (!data.length) {
            return renderEmptyState("export");
        }


        const currentItems =
            getCurrentItems("export");


        return (
            <div className="stock-table-wrapper">

                <div className="stock-table-title">

                    <div>
                        <h5>
                            <FontAwesomeIcon
                                icon={faPlaneDeparture}
                            />
                            Export Stock
                        </h5>

                        <span>
                            {data.length} total records
                        </span>
                    </div>

                </div>


                <div className="table-responsive">

                    <Table
                        bordered
                        hover
                        className="stock-data-table"
                    >

                        <thead>

                            <tr>

                                <th>Sr No</th>
                                <th>SB No</th>
                                <th>SB Date</th>
                                <th>SB Request ID</th>
                                <th>SER No</th>
                                <th>SER Date</th>
                                <th>Airway Bill</th>
                                <th>Exporter</th>
                                <th>Destination</th>
                                <th>Airline</th>
                                <th>Packages</th>
                                <th>Weight</th>
                                <th>NSDL</th>
                                <th>DGDC Status</th>
                                <th>Hold</th>
                                {showHoldColumns && (
                                    <>
                                        <th>Hold Date</th>
                                        <th>Hold By</th>
                                    </>
                                )}

                            </tr>

                        </thead>


                        <tbody>

                            {currentItems.map(
                                (item, index) => {

                                    const srNo =
                                        ((currentPages.export - 1) *
                                            itemsPerPage) +
                                        index + 1;


                                    return (
                                        <tr key={item.id || srNo}>

                                            <td>{srNo}</td>

                                            <td>
                                                {item.sbNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.sbDate
                                                )}
                                            </td>

                                            <td>
                                                {item.sbRequestId}
                                            </td>

                                            <td>
                                                {item.serNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.serDate
                                                )}
                                            </td>

                                            <td>
                                                {item.airwayBillNo}
                                            </td>

                                            <td>
                                                {item.partyName}
                                            </td>

                                            <td>
                                                {item.countryOfDestination}
                                            </td>

                                            <td>
                                                {item.airlineName}
                                            </td>

                                            <td>
                                                {item.noOfPackages}
                                            </td>

                                            <td>
                                                {formatWeight(
                                                    item.grossWeight,
                                                    "GMS"
                                                )}
                                            </td>

                                            <td>
                                                {item.nsdlStatus}
                                            </td>

                                            <td>
                                                {item.dgdcStatus}
                                            </td>

                                            <td>
                                                <span
                                                    className={
                                                        item.holdStatus === "Y"
                                                            ? "status-badge status-hold"
                                                            : "status-badge status-clear"
                                                    }
                                                >
                                                    {item.holdStatus === "Y"
                                                        ? "HOLD"
                                                        : "CLEAR"
                                                    }
                                                </span>
                                            </td>

                                            {showHoldColumns && (
                                                <>
                                                    <td>
                                                        {formatDate(
                                                            item.holdDate
                                                        )}
                                                    </td>

                                                    <td>
                                                        {item.holdBy}
                                                    </td>
                                                </>
                                            )}

                                        </tr>
                                    );

                                }
                            )}

                        </tbody>


                        <tfoot>

                            <tr>

                                <td
                                    colSpan="10"
                                    className="total-label"
                                >
                                    TOTAL
                                </td>

                                <td className="total-value">
                                    {totals.export.packages}
                                </td>

                                <td className="total-value">
                                    {totals.export.weight.toFixed(2)}
                                </td>

                                <td colSpan={showHoldColumns ? 5 : 3}></td>

                            </tr>

                        </tfoot>

                    </Table>

                </div>


                {renderPagination("export")}

            </div>
        );
    };


    /* =========================================================
       SUB IMPORT TABLE
    ========================================================= */

    const renderSubImportTable = () => {

        const data = stockData.subImport;

        if (!data.length) {
            return renderEmptyState("subImport");
        }


        const currentItems =
            getCurrentItems("subImport");


        return (
            <div className="stock-table-wrapper">

                <div className="stock-table-title">

                    <div>
                        <h5>
                            <FontAwesomeIcon
                                icon={faBoxesStacked}
                            />
                            Sub Import Stock
                        </h5>

                        <span>
                            {data.length} total records
                        </span>
                    </div>

                </div>


                <div className="table-responsive">

                    <Table
                        bordered
                        hover
                        className="stock-data-table"
                    >

                        <thead>

                            <tr>

                                <th>Sr No</th>
                                <th>Request ID</th>
                                <th>SIR No</th>
                                <th>SIR Date</th>
                                <th>Challan No</th>
                                <th>Challan Date</th>
                                <th>Packages</th>
                                <th>Net Weight</th>
                                <th>Weight Unit</th>
                                <th>NSDL</th>
                                <th>DGDC Status</th>
                                <th>SB No</th>
                                <th>SB Date</th>
                                <th>Party Name</th>

                            </tr>

                        </thead>


                        <tbody>

                            {currentItems.map(
                                (item, index) => {

                                    const srNo =
                                        ((currentPages.subImport - 1) *
                                            itemsPerPage) +
                                        index + 1;


                                    return (
                                        <tr key={item.id || srNo}>

                                            <td>{srNo}</td>

                                            <td>
                                                {item.requestId}
                                            </td>

                                            <td>
                                                {item.sirNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.sirDate
                                                )}
                                            </td>

                                            <td>
                                                {item.challanNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.challanDate
                                                )}
                                            </td>

                                            <td>
                                                {item.nop}
                                            </td>

                                            <td>
                                                {item.netWeight}
                                            </td>

                                            <td>
                                                {item.netWeightUnit}
                                            </td>

                                            <td>
                                                {item.nsdlStatus}
                                            </td>

                                            <td>
                                                {item.dgdcStatus}
                                            </td>

                                            <td>
                                                {item.sbNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.sbDate
                                                )}
                                            </td>

                                            <td>
                                                {item.partyName}
                                            </td>

                                        </tr>
                                    );

                                }
                            )}

                        </tbody>


                        <tfoot>

                            <tr>

                                <td
                                    colSpan="6"
                                    className="total-label"
                                >
                                    TOTAL
                                </td>

                                <td className="total-value">
                                    {totals.subImport.packages}
                                </td>

                                <td className="total-value">
                                    {totals.subImport.weight.toFixed(2)}
                                </td>

                                <td colSpan="6"></td>

                            </tr>

                        </tfoot>

                    </Table>

                </div>


                {renderPagination("subImport")}

            </div>
        );
    };


    /* =========================================================
       SUB EXPORT TABLE
    ========================================================= */

    const renderSubExportTable = () => {

        const data = stockData.subExport;

        if (!data.length) {
            return renderEmptyState("subExport");
        }


        const currentItems =
            getCurrentItems("subExport");


        return (
            <div className="stock-table-wrapper">

                <div className="stock-table-title">

                    <div>
                        <h5>
                            <FontAwesomeIcon
                                icon={faBoxOpen}
                            />
                            Sub Export Stock
                        </h5>

                        <span>
                            {data.length} total records
                        </span>
                    </div>

                </div>


                <div className="table-responsive">

                    <Table
                        bordered
                        hover
                        className="stock-data-table"
                    >

                        <thead>

                            <tr>

                                <th>Sr No</th>
                                <th>Request ID</th>
                                <th>SER No</th>
                                <th>SER Date</th>
                                <th>Challan No</th>
                                <th>Challan Date</th>
                                <th>Packages</th>
                                <th>GW Weight</th>
                                <th>Weight Unit</th>
                                <th>Party Name</th>

                            </tr>

                        </thead>


                        <tbody>

                            {currentItems.map(
                                (item, index) => {

                                    const srNo =
                                        ((currentPages.subExport - 1) *
                                            itemsPerPage) +
                                        index + 1;


                                    return (
                                        <tr key={item.id || srNo}>

                                            <td>{srNo}</td>

                                            <td>
                                                {item.requestId}
                                            </td>

                                            <td>
                                                {item.serNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.serDate
                                                )}
                                            </td>

                                            <td>
                                                {item.challanNo}
                                            </td>

                                            <td>
                                                {formatDate(
                                                    item.challanDate
                                                )}
                                            </td>

                                            <td>
                                                {item.nop}
                                            </td>

                                            <td>
                                                {item.gwWeight}
                                            </td>

                                            <td>
                                                {item.gwWeightUnit}
                                            </td>

                                            <td>
                                                {item.partyName}
                                            </td>

                                        </tr>
                                    );

                                }
                            )}

                        </tbody>


                        <tfoot>

                            <tr>

                                <td
                                    colSpan="6"
                                    className="total-label"
                                >
                                    TOTAL
                                </td>

                                <td className="total-value">
                                    {totals.subExport.packages}
                                </td>

                                <td className="total-value">
                                    {totals.subExport.weight.toFixed(2)}
                                </td>

                                <td colSpan="2"></td>

                            </tr>

                        </tfoot>

                    </Table>

                </div>


                {renderPagination("subExport")}

            </div>
        );
    };


    /* =========================================================
       ACTIVE CONTENT
    ========================================================= */

    const renderActiveContent = () => {

        switch (activeTab) {

            case "import":
                return renderImportTable();

            case "export":
                return renderExportTable();

            case "subImport":
                return renderSubImportTable();

            case "subExport":
                return renderSubExportTable();

            default:
                return renderImportTable();
        }
    };


    /* =========================================================
       XLS DOWNLOAD
    ========================================================= */

    const handleXLSdownLoad = async () => {

        setLoading(true);

        try {

            const response =
                await Rate_Chart_Service.downLoadStockReportXLS(
                    searchCriteria
                );


            if (response.status === 200) {

                const blob = new Blob(
                    [response.data],
                    {
                        type:
                            response.headers["content-type"]
                    }
                );


                const url =
                    window.URL.createObjectURL(blob);


                const a =
                    document.createElement("a");

                a.href = url;

                a.download =
                    "Stock_Report.xlsx";

                document.body.appendChild(a);

                a.click();

                window.URL.revokeObjectURL(url);

                document.body.removeChild(a);
            }

        } catch (error) {

            toast.error(
                "Error downloading file!",
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 800
                }
            );

        } finally {

            setLoading(false);
        }
    };


    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <>

            {loading && (
                <div style={styles.overlay}>

                    <ReactLoading
                        type="spin"
                        color="#0000FF"
                        height={100}
                        width={80}
                    />

                </div>
            )}


            <div className="Container">

                <h5 className="pageHead stock-page-heading">

                    <FontAwesomeIcon
                        icon={faReceipt}
                        className="stock-heading-icon"
                    />

                    Stock Report

                </h5>


                <Card className="stock-report-card">

                    <CardBody>

                        {/* =========================================
                            SEARCH SECTION
                        ========================================== */}

                        <div className="stock-filter-panel">

                            <div className="stock-filter-title">

                                <div>
                                    <span className="filter-title-icon">
                                        <FontAwesomeIcon
                                            icon={faSearch}
                                        />
                                    </span>

                                    <div>
                                        <h6>
                                            Stock Search
                                        </h6>

                                        <small>
                                            Select criteria to view stock
                                        </small>
                                    </div>
                                </div>

                            </div>


                            <Row>

                                <Col md={3}>

                                    <FormGroup>

                                        <Label
                                            className="forlabel"
                                            for="type"
                                        >
                                            TYPE
                                        </Label>


                                        <Input
                                            type="select"
                                            name="type"
                                            id="type"
                                            className="form-control inputField"
                                            value={
                                                searchCriteria.type
                                            }
                                            onChange={(e) =>
                                                setSearchCriteria({
                                                    ...searchCriteria,
                                                    type: e.target.value
                                                })
                                            }
                                        >

                                            <option value="ALL">
                                                ALL
                                            </option>

                                            <option value="IMPORT">
                                                IMPORT
                                            </option>

                                            <option value="EXPORT">
                                                EXPORT
                                            </option>

                                            <option value="SUB-EXPORT">
                                                SUB-EXPORT
                                            </option>

                                            <option value="SUB-IMPORT">
                                                SUB-IMPORT
                                            </option>

                                        </Input>

                                    </FormGroup>

                                </Col>


                                <Col md={3}>

                                    <FormGroup>

                                        <Label
                                            className="forlabel"
                                            for="location"
                                        >
                                            LOCATION
                                        </Label>


                                        <Input
                                            type="select"
                                            name="location"
                                            id="location"
                                            className="form-control inputField"
                                            value={
                                                searchCriteria.location
                                            }
                                            onChange={(e) =>
                                                setSearchCriteria({
                                                    ...searchCriteria,
                                                    location: e.target.value
                                                })
                                            }
                                        >

                                            <option value="SEEPZ">
                                                SEEPZ
                                            </option>

                                            <option value="CARGO">
                                                CARGO
                                            </option>

                                        </Input>

                                    </FormGroup>

                                </Col>


                                <Col md={3}>

                                    <FormGroup>

                                        <Label
                                            className="forlabel"
                                            for="holdStatus"
                                        >
                                            HOLD STATUS
                                        </Label>


                                        <Input
                                            type="select"
                                            name="holdStatus"
                                            id="holdStatus"
                                            className="form-control inputField"
                                            value={
                                                searchCriteria.holdStatus
                                            }
                                            onChange={(e) =>
                                                setSearchCriteria({
                                                    ...searchCriteria,
                                                    holdStatus: e.target.value
                                                })
                                            }
                                        >

                                            <option value="Y">
                                                YES
                                            </option>

                                            <option value="N">
                                                NO
                                            </option>

                                        </Input>

                                    </FormGroup>

                                </Col>


                                <Col
                                    md={3}
                                    className="stock-action-col"
                                >

                                    <Button
                                        color="primary"
                                        outline
                                        onClick={() =>
                                            handleSearch(
                                                searchCriteria
                                            )
                                        }
                                        className="stock-action-button"
                                    >

                                        <FontAwesomeIcon
                                            icon={faSearch}
                                        />

                                        SEARCH

                                    </Button>


                                    <Button
                                        color="danger"
                                        outline
                                        onClick={handleReset}
                                        className="stock-action-button"
                                    >

                                        <FontAwesomeIcon
                                            icon={faSyncAlt}
                                        />

                                        RESET

                                    </Button>

                                </Col>

                            </Row>

                        </div>


                        {/* =========================================
                            SUMMARY
                        ========================================== */}

                        <div className="stock-summary">

                            <div className="summary-card summary-import">

                                <FontAwesomeIcon
                                    icon={faPlaneArrival}
                                />

                                <div>
                                    <span>IMPORT</span>
                                    <strong>
                                        {stockData.import.length}
                                    </strong>
                                </div>

                            </div>


                            <div className="summary-card summary-export">

                                <FontAwesomeIcon
                                    icon={faPlaneDeparture}
                                />

                                <div>
                                    <span>EXPORT</span>
                                    <strong>
                                        {stockData.export.length}
                                    </strong>
                                </div>

                            </div>


                            <div className="summary-card summary-sub-import">

                                <FontAwesomeIcon
                                    icon={faBoxesStacked}
                                />

                                <div>
                                    <span>SUB IMPORT</span>
                                    <strong>
                                        {stockData.subImport.length}
                                    </strong>
                                </div>

                            </div>


                            <div className="summary-card summary-sub-export">

                                <FontAwesomeIcon
                                    icon={faBoxOpen}
                                />

                                <div>
                                    <span>SUB EXPORT</span>
                                    <strong>
                                        {stockData.subExport.length}
                                    </strong>
                                </div>

                            </div>

                        </div>


                        {/* =========================================
                            TOOLBAR
                        ========================================== */}

                        <div className="stock-toolbar">

                            <div>
                                <span className="toolbar-title">
                                    Stock Details
                                </span>

                                <span className="toolbar-subtitle">
                                    All available stock categories
                                </span>
                            </div>


                            <Button
                                color="success"
                                outline
                                onClick={
                                    handleXLSdownLoad
                                }
                                className="stock-xls-button"
                            >

                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                />

                                XLS

                            </Button>

                        </div>


                        {/* =========================================
                            TABS
                        ========================================== */}

                        <div className="stock-tabs-section">

                            {renderTabs()}


                            <div className="stock-tab-panel">

                                {renderActiveContent()}

                            </div>

                        </div>

                    </CardBody>

                </Card>

            </div>

        </>
    );
}
